import React, { useEffect, useState } from "react";
import { useForm, Controller } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as Yup from "yup";
import Input from "../boxes/Input";
import Dropdown from "../boxes/Dropdown";
import { logAnalyticsEvent } from "../../firebaseInit";
import { eventName } from "../../services/constants";
import { useTranslation } from "react-i18next";

const ContactDetails = ({
  defaultValues,
  setContactDetails,
  states,
  statesOptions,
  lgas,
  onClose,
  pharmacies,
  MODALNAVIGATOR,
  rfcPharmacies,
  ...props
}) => {
  const { t } = useTranslation(["consultations"]);
  const [stage, setStage] = useState(1);

  const { prescriptions } = props;

  logAnalyticsEvent(
    eventName.VIEW_PRESCRIPTION_CONTACT_DETAILS,

    {
      item_name: prescriptions?.id,
    }
  );

  return (
    <div>
      <h2 className="text-[#323539] text-[24px] font-bold leading-[32px] my-6">
        {t("contactDetailsHeading")}
      </h2>

      <p className="text-[18px] leading-[24px] text-[#383D43] font-bold mb-[2px]">
        {t("contactDetailsSubHeading")}
      </p>

      {stage === 1 ? (
        <StageOne
          pharmacies={pharmacies}
          rfcPharmacies={rfcPharmacies}
          setContactDetails={setContactDetails}
          defaultValues={defaultValues}
          states={states}
          statesOptions={statesOptions}
          lgas={lgas}
          onClose={onClose}
          MODALNAVIGATOR={MODALNAVIGATOR}
          setStage={setStage}
          props={props}
        />
      ) : (
        <StageTwo
          pharmacies={pharmacies}
          rfcPharmacies={rfcPharmacies}
          setContactDetails={setContactDetails}
          defaultValues={defaultValues}
          onClose={onClose}
          MODALNAVIGATOR={MODALNAVIGATOR}
          props={props}
        />
      )}
    </div>
  );
};

export default ContactDetails;

const StageOne = ({
  setContactDetails,
  defaultValues,
  states,
  statesOptions,
  lgas,
  setStage,
  onClose,
}) => {
  const { t } = useTranslation(["consultations"]);
  const [lgaOptions, setLgaOptions] = useState([]);
  const [openDropdown, setOpenDropdown] = useState(null);

  const contactDetailsSchema = Yup.object().shape({
    state: Yup.string().required(t("requiredStateErrorMessage")),
    lga: Yup.object().required(t("requiredLgaErrorMessage")),
    address: Yup.string().required(t("requiredStreetAddressErrorMessage")),
    landmark: Yup.string().required(t("requiredLandmarkErrorMessage")),
  });

  const {
    control,
    handleSubmit,
    watch,
    formState: { errors, isValid },
    resetField,
  } = useForm({
    mode: "onChange",
    resolver: yupResolver(contactDetailsSchema),
    defaultValues,
  });

  // Watch state changes
  const selectedState = watch("state");

  useEffect(() => {
    // Update lgaOptions whenever selectedState changes
    if (selectedState) {
      const stateId = getIdByName(selectedState);
      const options = getStatesByStateId(stateId);

      setLgaOptions(
        options?.map((item) => ({
          value: item.id,
          label: item.name,
        }))
      );
      resetField("lga");
    } else {
      setLgaOptions([]); // Reset lgaOptions if no state is selected
      resetField("lga");
    }
  }, [selectedState]);

  const onSubmit = (data) => {
    setContactDetails({ ...data });
    setStage(2);
  };

  const getIdByName = (name) => {
    const result = states.find((item) => item.name === name);
    return result ? result.id : null; // Returns the id or null if not found
  };

  const getStatesByStateId = (stateId) => {
    return lgas?.filter((item) => item.state_id === stateId);
  };

  return (
    <form
      className="flex flex-col mt-[2px] gap-8"
      onSubmit={handleSubmit(onSubmit)}
    >
      <Controller
        name="state"
        control={control}
        render={({ field }) => {
          return (
            <Dropdown
              data-testid="e2e-dropdown-state"
              label={t("contactDetailsForm.stateOfResidenceLabel")}
              name="states"
              placeholder={t("contactDetailsForm.stateOfResidencePlaceholder")}
              id="states"
              {...field}
              options={statesOptions}
              error={errors.state?.message}
              isOpen={openDropdown === "state"}
              setIsOpen={() =>
                setOpenDropdown(openDropdown === "state" ? null : "state")
              }
            />
          );
        }}
      />

      <Controller
        name="lga"
        control={control}
        render={({ field }) => {
          return (
            <Dropdown
              data-testid="e2e-dropdown-lga"
              label={t("contactDetailsForm.lgaLabel")}
              name="lgas"
              placeholder={t("contactDetailsForm.lgaPlaceholder")}
              id="lgas"
              {...field}
              options={lgaOptions}
              error={errors.lga?.message}
              isOpen={openDropdown === "lga"} // Only open if it's the selected one
              setIsOpen={() =>
                setOpenDropdown(openDropdown === "lga" ? null : "lga")
              } // Toggle dropdown
            />
          );
        }}
      />

      <Controller
        name="address"
        control={control}
        render={({ field }) => {
          return (
            <Input
              label={t("contactDetailsForm.streetAddressLabel")}
              name="street_address"
              id="street_address"
              placeholder={t("contactDetailsForm.streetAddressPlaceholder")}
              type="text"
              {...field}
              error={errors.address?.message}
            />
          );
        }}
      />

      <Controller
        name="landmark"
        control={control}
        render={({ field }) => {
          return (
            <Input
              label={t("contactDetailsForm.landmarkLabel")}
              name="landmark"
              id="landmark"
              placeholder={t("contactDetailsForm.landmarkPlaceholder")}
              type="text"
              {...field}
              error={errors.landmark?.message}
            />
          );
        }}
      />

      <div className="flex items-center gap-2">
        <button
          className="w-1/2 h-[54px] bg-white border border-[#E5E5E7] py-3 rounded-[128px] text-[#323539] text-[15px] leading-[22px]"
          type="button"
          onClick={onClose}
        >
          {t("contactDetailsForm.cancelButtonText")}
        </button>
        <button
          id="e2e-continue-to-address2"
          className={`w-1/2 h-[54px] ${
            isValid ? "bg-[#094063] text-white" : "bg-[#F8F9FB] text-[#E5E5E7]"
          } border border-[#E5E5E7] py-3 rounded-[128px] text-[15px] leading-[22px]`}
          type="submit"
          disabled={!isValid}
        >
          {t("contactDetailsForm.nextButtonText")}
        </button>
      </div>
    </form>
  );
};
const StageTwo = ({
  pharmacies,
  rfcPharmacies,
  setContactDetails,
  defaultValues,
  onClose,
  MODALNAVIGATOR,
  props,
}) => {
  const { t } = useTranslation(["consultations"]);
  const phoneNumberRegex = /^(0|\+2340?)[789]\d{9}$/;
  const contactDetailsSchema = Yup.object().shape({
    phoneNumber: Yup.string()
      .required(t("contactDetailsForm.requiredPhoneNumberErrorMessage"))
      .matches(
        phoneNumberRegex,
        t("contactDetailsForm.invalidPhoneNumberErrorMessage")
      ),

    alternativePhoneNumber: Yup.string()
      .required(
        t("contactDetailsForm.requiredAlternativePhoneNumberErrorMessage")
      )
      .matches(
        phoneNumberRegex,
        t("contactDetailsForm.invalidAlternativePhoneNumberErrorMessage")
      ),
  });

  const {
    control,
    handleSubmit,
    formState: { errors, isValid },
  } = useForm({
    mode: "onChange",
    resolver: yupResolver(contactDetailsSchema),
    defaultValues,
  });
  const onSubmit = (data) => {
    const hasDelivery = () =>
      pharmacies.some(
        (pharm) =>
          pharm.state === data.state &&
          pharm.has_home_delivery.toLowerCase() === "yes"
      );

    const isRfcPresent = rfcPharmacies.some(
      (pharmacy) => pharmacy.state_object.name === data.state
    );
    setContactDetails({ ...data, isRfcPresent, hasDelivery: hasDelivery() });
    props.setModalContent(MODALNAVIGATOR.fullfillmentOptions);
  };
  return (
    <form
      className="flex flex-col mt-8 gap-8"
      onSubmit={handleSubmit(onSubmit)}
    >
      <Controller
        name="phoneNumber"
        control={control}
        render={({ field }) => {
          return (
            <Input
              label={t("contactDetailsForm.phoneNumberLabel")}
              name="phone_number"
              id="phone_number"
              placeholder={t("contactDetailsForm.phoneNumberPlaceholder")}
              type="tel"
              {...field}
              error={errors.phoneNumber?.message}
              errorId={"e2e-phoneNumberError"}
            />
          );
        }}
      />

      <Controller
        name="alternativePhoneNumber"
        control={control}
        render={({ field }) => {
          return (
            <div>
              <Input
                label={t("contactDetailsForm.alternativePhoneNumberLabel")}
                name="alternative_phone_number"
                id="alternative_phone_number"
                placeholder={t("contactDetailsForm.phoneNumberPlaceholder")}
                type="tel"
                {...field}
                error={errors.alternativePhoneNumber?.message}
                errorId={"e2e-alternativePhoneNumberError"}
              />
              <p className="text-[#858C95] text-[14px] leading-[20px] mt-4 w-[480px] e2e-alternativePhoneNumberInfo ">
                {t("contactDetailsForm.alternativePhoneNumberInfo")}
              </p>
            </div>
          );
        }}
      />

      <div className="flex items-center gap-2">
        <button
          className="w-1/2 h-[54px] text-[15px] leading-[22px] bg-white border border-[#E5E5E7] py-3 rounded-[128px] text-[#323539]"
          type="button"
          onClick={onClose}
        >
          {t("contactDetailsForm.cancelButtonText")}
        </button>
        <button
          id="e2e-continue-to-delivery-options"
          className={`w-1/2 h-[54px] ${
            isValid ? "bg-[#094063] text-white" : "bg-[#F8F9FB] text-[#E5E5E7]"
          }  border border-[#E5E5E7] py-3 rounded-[128px] text-[15px] leading-[22px]`}
          type="submit"
          disabled={!isValid}
        >
          {t("contactDetailsForm.proceedButtonText")}
        </button>
      </div>
    </form>
  );
};
